import React from 'react';
import { Form, Input } from 'antd';
import type { NamePath } from 'antd/es/form/interface';
import { checkProviderEmail } from 'api/group';

type CheckResult = { addable: boolean; message?: string } | null;
export type ProviderEmailCheckFn = (email: string) => Promise<CheckResult>;

// 默认检查：团队邀请口径 /provider/team-member/check-provider-email；
// 场景口径不同时（如 insurance billing 的 rendering provider 名单）由调用方经 checkEmail prop 注入
const defaultCheckEmail: ProviderEmailCheckFn = async (email) => {
    const { data } = await checkProviderEmail(email);
    if (data?.code !== 200 || !data?.data) return null;
    return { addable: !!data.data.addable, message: data.data.message };
};

// 按检查函数隔离的缓存（同一邮箱在多行 / 反复 blur 间只请求一次）。
// 只缓存成功判定：检查失败不入缓存，重新 blur 可重试。
// 注意：邀请/激活成功后同邮箱的 addable 会变化，需要重查时先清缓存
const cachesByFn = new Map<ProviderEmailCheckFn, Record<string, CheckResult>>();

export const clearProviderEmailCheckCache = () => cachesByFn.clear();

const checkEmailCached = async (fn: ProviderEmailCheckFn, trimmed: string): Promise<CheckResult> => {
    let cache = cachesByFn.get(fn);
    if (!cache) {
        cache = {};
        cachesByFn.set(fn, cache);
    }
    if (trimmed in cache) return cache[trimmed];
    const result = await fn(trimmed);
    if (result) cache[trimmed] = result;
    return result;
};

type Props = {
    /** Form.Item name（须挂在 antd Form 内使用） */
    name: NamePath;
    label?: React.ReactNode;
    placeholder?: string;
    size?: 'small' | 'middle' | 'large';
    disabled?: boolean;
    /** 后端查重函数；默认团队邀请口径。须传模块级稳定引用，否则缓存无法命中 */
    checkEmail?: ProviderEmailCheckFn;
    /** 本地查重：返回同表单其它行已填的 email（不含当前行），命中即报「已添加」，不发请求 */
    getSiblingEmails?: () => (string | undefined)[];
    /** 查重结果回调：'duplicate' = 不可用；'unverifiable' = 检查失败无法判定；undefined = 通过 / 为空。
     *  可写入表单辅助字段做提交拦截 */
    onStatusChange?: (status?: 'duplicate' | 'unverifiable') => void;
};

/**
 * 带「是否已添加」检查的 provider email 输入框。
 * blur 触发校验：格式 → 本地查重（getSiblingEmails）→ 后端 checkEmail（带缓存）。
 * 检查接口失败不放行（记 'unverifiable' 拦截），避免把「查不到」当「可用」。
 */
export default function ProviderEmailInput({
    name,
    label = 'Email',
    placeholder = 'provider@example.com',
    size,
    disabled,
    checkEmail = defaultCheckEmail,
    getSiblingEmails,
    onStatusChange,
}: Props) {
    const validateAddable = async (_: unknown, value?: string) => {
        const trimmed = (value || '').trim();
        if (!trimmed) {
            onStatusChange?.(undefined);
            return;
        }
        const siblings = getSiblingEmails?.() || [];
        const dupLocal = siblings.some((e) => (e || '').trim().toLowerCase() === trimmed.toLowerCase());
        if (dupLocal) {
            onStatusChange?.('duplicate');
            throw new Error('This email has already been added to another provider below');
        }
        let result: CheckResult = null;
        try {
            result = await checkEmailCached(checkEmail, trimmed);
        } catch (e) {
            result = null;
        }
        if (!result) {
            onStatusChange?.('unverifiable');
            throw new Error('We could not verify this email right now. Please try again.');
        }
        if (!result.addable) {
            onStatusChange?.('duplicate');
            throw new Error(result.message || 'This email has already been added to your practice');
        }
        onStatusChange?.(undefined);
    };

    return (
        <Form.Item
            name={name}
            label={label}
            validateTrigger="onBlur"
            validateFirst
            rules={[{ type: 'email', message: 'Enter a valid email' }, { validator: validateAddable }]}
        >
            <Input size={size} type="email" disabled={disabled} placeholder={placeholder} />
        </Form.Item>
    );
}
